import { useTranslation } from 'react-i18next';
import { isSimpleKind } from '@magimp/shared';
import type { DraftScratchpad } from './types.js';
import { kindHidesInsurance } from './insurance-visibility.js';

export function useDraftCompleteness(d: DraftScratchpad): string[] {
  const { t } = useTranslation(['requests']);
  const requiredErrors: string[] = [];
  if (!d.name.trim()) requiredErrors.push(t('requests:draft.required.name'));
  const isContainer = d.kind === 'CONTAINER';
  const isSimple = isSimpleKind(d.kind);
  // WHY (#1448): STANDARD is the only non-SIMPLE non-CONTAINER kind. Its
  //   redesigned form gates on request-level weight/volume (below) + the
  //   shared name/destinations checks, NOT on items/per-item/boxes.
  const isStandard = !isSimple && !isContainer;
  // WHY (#700 / #1057): STANDARD requires at least one destination;
  //   CONTAINER requires a non-empty deliveryAddress. The two paths are
  //   mutually exclusive — wire-side write/save invariants reject the
  //   wrong-shape value for the kind — so the completeness gate tracks
  //   only the field that's actually surfaced for the active editor.
  if (!isContainer && d.destinations.length === 0) {
    requiredErrors.push(t('requests:draft.required.destinations'));
  }
  if (isContainer && (d.deliveryAddress === null || d.deliveryAddress.trim().length === 0)) {
    requiredErrors.push(t('requests:draft.required.deliveryAddress'));
  }
  // WHY (#1393, BR-5): an empty pickup address blocks send, same as a
  //   missing delivery address. Mirror of the deliveryAddress gate.
  if (isContainer && (d.pickupAddress === null || d.pickupAddress.trim().length === 0)) {
    requiredErrors.push(t('requests:draft.required.pickupAddress'));
  }
  // WHY (#701): CONTAINER must have a transportType selected before
  //   SEND fires; this is a header gate, not an item gate.
  if (isContainer && d.transportTypeId === null) {
    requiredErrors.push(t('requests:draft.required.transportType'));
  }
  // WHY (#1448 / #2116): STANDARD AND CONTAINER send-readiness gate on the
  //   request-level totals instead of per-item weight/volume. Both must be
  //   present and > 0 (name + the per-kind destination/address checks are
  //   covered above). This mirrors the backend transition-readiness gate,
  //   which the wire reasserts on the IN_WORK transition.
  if (isStandard || isContainer) {
    if (d.totalWeight === null || d.totalWeight <= 0)
      requiredErrors.push(t('requests:draft.required.weight'));
    if (d.totalVolume === null || d.totalVolume <= 0)
      requiredErrors.push(t('requests:draft.required.volume'));
  }
  // WHY (#1446): CONTAINER preserves hidden legacy items/boxes as
  //   history and must NEVER block Send on their content. The redesigned
  //   CONTAINER form removed the Positions section entirely; send-readiness
  //   is header-only (name + transport type + pickup + delivery). All item
  //   and box content checks are skipped for CONTAINER.
  // WHY (#1448): STANDARD likewise preserves hidden legacy items/boxes as
  //   history and must NEVER block Send on their content — only SIMPLE owns
  //   and gates on items/boxes now.
  if (isSimple) {
    // WHY (#599): SEND-readiness now accepts a request that carries
    //   either ordinary items, boxes, or both. The empty-empty case
    //   surfaces a single combined toast (`itemOrBox`) instead of the
    //   legacy `item` toast so the affordance reads correctly when the
    //   user has the boxes section open.
    if (d.items.length === 0 && d.boxes.length === 0)
      requiredErrors.push(t('requests:draft.required.itemOrBox'));
    if (d.items.some((it) => !it.name.trim()))
      requiredErrors.push(t('requests:draft.required.itemName'));
    if (d.items.some((it) => !it.description.trim()))
      requiredErrors.push(t('requests:draft.required.itemDescription'));
    // WHY (#701): CONTAINER drafts relax per-item weight/volume — the
    //   parent's `transportType` row carries the canonical capacity.
    //   STANDARD drafts keep the strict positive bound.
    if (d.items.some((it) => it.weight === null || it.weight <= 0))
      requiredErrors.push(t('requests:draft.required.itemWeight'));
    if (d.items.some((it) => it.volume === null || it.volume <= 0))
      requiredErrors.push(t('requests:draft.required.itemVolume'));
    if (
      d.items.some(
        (it) => !Number.isInteger(it.quantity) || it.quantity < 1 || it.quantity > 999999,
      )
    )
      requiredErrors.push(t('requests:draft.required.itemQuantity'));
    if (d.items.length > 0 && d.items.some((it) => it.photos.length === 0))
      requiredErrors.push(t('requests:draft.required.itemPhoto'));
    // WHY (#599): nested-item invariants mirror the backend
    //   `computeDraftIncompleteMissing` rules so the local Send button
    //   state matches the wire gate. `quantity` is `null`-tolerant
    //   (blank means "unspecified"); a present non-positive value is a
    //   violation.
    if (d.boxes.some((b) => b.items.length === 0))
      requiredErrors.push(t('requests:draft.required.boxItem'));
    if (d.boxes.some((b) => b.items.some((it) => !it.name.trim())))
      requiredErrors.push(t('requests:draft.required.boxItemName'));
    if (d.boxes.some((b) => b.items.some((it) => !it.description.trim())))
      requiredErrors.push(t('requests:draft.required.boxItemDescription'));
    if (d.boxes.some((b) => b.items.some((it) => it.photos.length !== 1)))
      requiredErrors.push(t('requests:draft.required.boxItemPhoto'));
    if (
      d.boxes.some((b) =>
        b.items.some(
          (it) =>
            it.quantity !== null &&
            (!Number.isInteger(it.quantity) || it.quantity < 1 || it.quantity > 999_999),
        ),
      )
    )
      requiredErrors.push(t('requests:draft.required.boxItemQuantity'));
  }
  // WHY (#1061): manager-side insurance triplet — when the flag is on,
  //   both cargo value and currency must be present. The wire layer's
  //   `enforceInsuranceInvariants` rejects the half-filled shape too,
  //   so this gate keeps the local Send button affordance honest.
  // WHY (#1306): SIMPLE forces insurance off at the save layer, so a
  //   SIMPLE scratchpad with stale `insuranceRequested: true` must never
  //   block Send on the insurance triplet — skip the check entirely.
  // WHY (#1393): CONTAINER also hides insurance (same as SIMPLE), so its
  //   hidden triplet state must never block Send either.
  if (
    !kindHidesInsurance(d.kind) &&
    d.insuranceRequested === true &&
    (d.insuranceCargoValue === null ||
      d.insuranceCargoValue === undefined ||
      d.insuranceCargoCurrency === null ||
      d.insuranceCargoCurrency === undefined)
  ) {
    requiredErrors.push(t('requests:draft.required.insuranceTriplet'));
  }
  return requiredErrors;
}
